import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash2, Save, X, Fuel, BarChart3 } from "lucide-react";
import VesselCharts from "@/components/VesselCharts";

// Conversions — canonical storage is GPH + MPH.
const L_PER_GAL = 3.78541;
const KM_PER_MI = 1.60934;
const KNOTS_PER_MPH = 0.868976;
const NM_PER_MI = 0.868976; // 1 statute mile = 0.868976 nautical miles

const FUEL_UNITS = [
    { value: "gph", label: "GPH", short: "gph" },
    { value: "lph", label: "L/h", short: "L/h" },
];
const SPEED_UNITS = [
    { value: "mph", label: "MPH", short: "mph" },
    { value: "kph", label: "KPH", short: "kph" },
    { value: "knots", label: "Knots", short: "kt" },
];

// gph → display fuel
function fmtFuel(gph, unit) {
    if (gph == null) return "—";
    const v = unit === "lph" ? gph * L_PER_GAL : gph;
    return v.toFixed(1);
}
// mph → display speed
function fmtSpeed(mph, unit) {
    if (mph == null) return "—";
    let v = mph;
    if (unit === "kph") v = mph * KM_PER_MI;
    else if (unit === "knots") v = mph * KNOTS_PER_MPH;
    return v.toFixed(1);
}
// Land economy: MPG (or L/100km if KPH chosen)
function fmtEconomy(mph, gph, fuelUnit, speedUnit) {
    if (!gph || mph == null) return "—";
    if (fuelUnit === "lph" && speedUnit === "kph") {
        // L/100 km
        const lph = gph * L_PER_GAL;
        const kph = mph * KM_PER_MI;
        if (kph <= 0) return "—";
        return (lph / kph * 100).toFixed(2);
    }
    // Statute MPG (US gallons / statute mile)
    return (mph / gph).toFixed(2);
}
// Sea economy: nautical miles per gallon (or per litre)
function fmtSeaEconomy(mph, gph, fuelUnit) {
    if (!gph || mph == null) return "—";
    const nm_per_hr = mph * NM_PER_MI; // knots
    if (fuelUnit === "lph") {
        const lph = gph * L_PER_GAL;
        if (lph <= 0) return "—";
        return (nm_per_hr / lph).toFixed(2);
    }
    return (nm_per_hr / gph).toFixed(2);
}

function economyLabel(fuelUnit, speedUnit) {
    if (fuelUnit === "lph" && speedUnit === "kph") return "L/100km";
    return "MPG";
}
function seaEconomyLabel(fuelUnit) {
    return fuelUnit === "lph" ? "NM/L" : "nMPG";
}
function fuelInputLabel(fuelUnit) {
    return fuelUnit === "lph" ? "L/h" : "GPH";
}
function speedInputLabel(speedUnit) {
    if (speedUnit === "kph") return "KPH";
    if (speedUnit === "knots") return "Knots";
    return "MPH";
}

// Convert user-typed value back to canonical GPH / MPH for storage
function toCanonicalGph(value, unit) {
    const n = parseFloat(value);
    if (!Number.isFinite(n)) return null;
    return unit === "lph" ? n / L_PER_GAL : n;
}
function toCanonicalMph(value, unit) {
    const n = parseFloat(value);
    if (!Number.isFinite(n)) return null;
    if (unit === "kph") return n / KM_PER_MI;
    if (unit === "knots") return n / KNOTS_PER_MPH;
    return n;
}

export default function FuelEconomyView({ vessel, canWrite, onVesselPatched }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    const [engineFilter, setEngineFilter] = useState("all"); // "all" or specific engine_id

    // Vessel-level unit preferences (display only — values stored in GPH/MPH)
    const fuelUnit = vessel.fuel_unit || "gph";
    const speedUnit = vessel.speed_unit || "mph";
    const engines = vessel.engines || [];
    const isMultiEngine = engines.length >= 2;

    // Filter rows by selected engine. "all" returns everything; specific id
    // matches only that engine. Legacy rows with engine_id=null show only
    // under "all" so old data isn't hidden.
    const filteredRows = useMemo(() => {
        if (engineFilter === "all") return rows;
        return rows.filter((r) => r.engine_id === engineFilter);
    }, [rows, engineFilter]);

    const refresh = async () => {
        try {
            const { data } = await api.get(`/vessels/${vessel.id}/fuel-economy`);
            setRows(data);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load fuel economy.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vessel.id]);

    const updateUnit = async (key, value) => {
        try {
            const { data } = await api.patch(`/vessels/${vessel.id}`, { [key]: value });
            onVesselPatched?.(data);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't update units.");
        }
    };

    const removeRow = async (id) => {
        if (!window.confirm("Remove this RPM row?")) return;
        try {
            await api.delete(`/vessels/${vessel.id}/fuel-economy/${id}`);
            await refresh();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't delete row.");
        }
    };

    return (
        <div data-testid="fuel-economy-view">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 14, marginBottom: 20 }}>
                <div>
                    <h2 className="bm-serif" style={{ fontSize: 32, margin: 0, fontWeight: 500 }}>Fuel Economy</h2>
                    <p style={{ color: "var(--bm-text-muted)", margin: "4px 0 0", fontSize: 14 }}>
                        RPM × speed × fuel burn. Find your sweet spot. {rows.length} row{rows.length === 1 ? "" : "s"}.
                    </p>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <UnitToggle
                        testid="fuel-unit-toggle"
                        label="Fuel"
                        value={fuelUnit}
                        options={FUEL_UNITS}
                        disabled={!canWrite}
                        onChange={(v) => updateUnit("fuel_unit", v)}
                    />
                    <UnitToggle
                        testid="speed-unit-toggle"
                        label="Speed"
                        value={speedUnit}
                        options={SPEED_UNITS}
                        disabled={!canWrite}
                        onChange={(v) => updateUnit("speed_unit", v)}
                    />
                    {canWrite && (
                        <button data-testid="add-fuel-economy-btn" className="bm-btn bm-btn-gold" onClick={() => setShowAdd(true)}>
                            <Plus size={15} /> Add RPM row
                        </button>
                    )}
                </div>
            </div>

            {err && <div data-testid="fuel-economy-err" style={{ color: "var(--bm-bad)", marginBottom: 12, fontSize: 13 }}>{err}</div>}

            {isMultiEngine && (
                <div data-testid="fe-engine-filter" style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
                    <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--bm-text-dim)", marginRight: 4 }}>Engine</span>
                    <FilterPill
                        testid="fe-filter-all"
                        active={engineFilter === "all"}
                        onClick={() => setEngineFilter("all")}
                        label={`All (${rows.length})`}
                    />
                    {engines.map((eng) => {
                        const count = rows.filter((r) => r.engine_id === eng.id).length;
                        return (
                            <FilterPill
                                key={eng.id}
                                testid={`fe-filter-${(eng.label || eng.id).toLowerCase().replace(/\s+/g, "-")}`}
                                active={engineFilter === eng.id}
                                onClick={() => setEngineFilter(eng.id)}
                                label={`${eng.label} (${count})`}
                            />
                        );
                    })}
                </div>
            )}

            <div className="bm-card" style={{ overflow: "hidden", marginBottom: 18 }}>
                <div style={{ overflowX: "auto" }}>
                    <table className="bm-table">
                        <thead>
                            <tr>
                                {isMultiEngine && <th>Engine</th>}
                                <th>{fuelInputLabel(fuelUnit)}</th>
                                <th>RPM</th>
                                <th>{speedInputLabel(speedUnit)}</th>
                                {speedUnit !== "knots" && <th>Knots</th>}
                                <th>{economyLabel(fuelUnit, speedUnit)}</th>
                                <th>{seaEconomyLabel(fuelUnit)}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={(speedUnit !== "knots" ? 7 : 6) + (isMultiEngine ? 1 : 0)} style={{ textAlign: "center", padding: 26, color: "var(--bm-text-muted)" }}>Loading…</td></tr>
                            )}
                            {!loading && filteredRows.length === 0 && (
                                <tr><td colSpan={(speedUnit !== "knots" ? 7 : 6) + (isMultiEngine ? 1 : 0)} style={{ textAlign: "center", padding: 36, color: "var(--bm-text-muted)" }}>
                                    {rows.length === 0
                                        ? `No fuel economy data yet — ${canWrite ? "tap \"Add RPM row\" to log your first speed test." : "the owner hasn't added any rows yet."}`
                                        : "No rows for this engine yet."}
                                </td></tr>
                            )}
                            {!loading && filteredRows.map((r) => {
                                const eng = r.engine_id ? engines.find((e) => e.id === r.engine_id) : null;
                                return (
                                <tr key={r.id} data-testid={`fe-row-${r.id}`}>
                                    {isMultiEngine && (
                                        <td style={{ color: eng ? "var(--bm-gold)" : "var(--bm-text-dim)", fontSize: 12 }}>
                                            {eng?.label || "—"}
                                        </td>
                                    )}
                                    <td style={{ fontVariantNumeric: "tabular-nums" }}>{fmtFuel(r.gph, fuelUnit)}</td>
                                    <td style={{ fontVariantNumeric: "tabular-nums" }}>{r.rpm}</td>
                                    <td style={{ fontVariantNumeric: "tabular-nums" }}>{fmtSpeed(r.mph, speedUnit)}</td>
                                    {speedUnit !== "knots" && (
                                        <td style={{ color: "var(--bm-text-muted)", fontVariantNumeric: "tabular-nums" }}>{fmtSpeed(r.mph, "knots")}</td>
                                    )}
                                    <td style={{ color: "var(--bm-good)", fontVariantNumeric: "tabular-nums" }}>{fmtEconomy(r.mph, r.gph, fuelUnit, speedUnit)}</td>
                                    <td style={{ color: "var(--bm-blue)", fontVariantNumeric: "tabular-nums" }}>{fmtSeaEconomy(r.mph, r.gph, fuelUnit)}</td>
                                    <td style={{ textAlign: "right", display: "flex", gap: 4, justifyContent: "flex-end" }}>
                                        {canWrite && (
                                            <>
                                                <button data-testid={`fe-edit-${r.id}`} className="bm-btn bm-btn-ghost" onClick={() => setEditingId(r.id)} style={{ padding: "4px 8px", fontSize: 12 }}>Edit</button>
                                                <button data-testid={`fe-delete-${r.id}`} className="bm-btn bm-btn-danger" onClick={() => removeRow(r.id)} style={{ padding: "4px 8px" }}>
                                                    <Trash2 size={12} />
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {rows.length >= 2 && (
                <MpgChart
                    rows={filteredRows.length >= 2 ? filteredRows : rows}
                    fuelUnit={fuelUnit}
                    speedUnit={speedUnit}
                    engines={engines}
                    showPerEngine={isMultiEngine && engineFilter === "all"}
                />
            )}

            <VesselCharts vessel={vessel} />

            {showAdd && (
                <FuelEconomyEditModal
                    vesselId={vessel.id}
                    fuelUnit={fuelUnit}
                    speedUnit={speedUnit}
                    engines={engines}
                    defaultEngineId={engineFilter !== "all" ? engineFilter : (engines[0]?.id || null)}
                    onClose={() => setShowAdd(false)}
                    onSaved={() => { setShowAdd(false); refresh(); }}
                />
            )}
            {editingId && (
                <FuelEconomyEditModal
                    vesselId={vessel.id}
                    fuelUnit={fuelUnit}
                    speedUnit={speedUnit}
                    engines={engines}
                    initial={rows.find((r) => r.id === editingId)}
                    onClose={() => setEditingId(null)}
                    onSaved={() => { setEditingId(null); refresh(); }}
                />
            )}
        </div>
    );
}

function UnitToggle({ testid, label, value, options, disabled, onChange }) {
    return (
        <div data-testid={testid} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--bm-text-dim)" }}>{label}</span>
            <div style={{ display: "flex", border: "1px solid var(--bm-border-soft)", borderRadius: "999px", overflow: "hidden", background: "rgba(8,22,40,0.5)" }}>
                {options.map((opt) => (
                    <button
                        key={opt.value}
                        type="button"
                        data-testid={`${testid}-${opt.value}`}
                        onClick={() => onChange(opt.value)}
                        disabled={disabled}
                        style={{
                            padding: "5px 12px",
                            fontSize: 12,
                            border: "none",
                            background: value === opt.value ? "rgba(212,181,114,0.20)" : "transparent",
                            color: value === opt.value ? "var(--bm-gold)" : "var(--bm-text-muted)",
                            cursor: disabled ? "not-allowed" : "pointer",
                            fontWeight: value === opt.value ? 600 : 400,
                        }}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

// Simple pill toggle used for the per-engine filter on multi-engine vessels.
function FilterPill({ testid, active, onClick, label }) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            style={{
                padding: "5px 12px",
                fontSize: 12,
                border: `1px solid ${active ? "var(--bm-gold)" : "var(--bm-border-soft)"}`,
                borderRadius: "999px",
                background: active ? "rgba(212,181,114,0.20)" : "rgba(8,22,40,0.5)",
                color: active ? "var(--bm-gold)" : "var(--bm-text-muted)",
                cursor: "pointer",
                fontWeight: active ? 600 : 400,
            }}
        >
            {label}
        </button>
    );
}

function FuelEconomyEditModal({ vesselId, fuelUnit, speedUnit, engines = [], initial, defaultEngineId = null, onClose, onSaved }) {
    // Show the form in whichever units the user is viewing in, then convert
    // back to canonical GPH/MPH on save.
    const initFuelDisplay = initial ? fmtFuel(initial.gph, fuelUnit) : "";
    const initSpeedDisplay = initial ? fmtSpeed(initial.mph, speedUnit) : "";
    const [rpm, setRpm] = useState(initial ? String(initial.rpm) : "");
    const [fuel, setFuel] = useState(initFuelDisplay);
    const [speed, setSpeed] = useState(initSpeedDisplay);
    const [engineId, setEngineId] = useState(initial?.engine_id ?? defaultEngineId ?? "");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const isMultiEngine = engines.length >= 2;

    const save = async () => {
        setErr(""); setBusy(true);
        const rpmN = parseInt(rpm, 10);
        const gph = toCanonicalGph(fuel, fuelUnit);
        const mph = toCanonicalMph(speed, speedUnit);
        if (!Number.isFinite(rpmN) || rpmN < 0 || rpmN > 20000) {
            setErr("RPM must be a number between 0 and 20000.");
            setBusy(false);
            return;
        }
        if (gph == null || gph < 0 || gph > 200) {
            setErr("Fuel burn looks off. Try a smaller value.");
            setBusy(false);
            return;
        }
        if (mph == null || mph < 0 || mph > 200) {
            setErr("Speed looks off. Try a smaller value.");
            setBusy(false);
            return;
        }
        try {
            const body = { rpm: rpmN, gph, mph, engine_id: engineId || null };
            if (initial) {
                await api.patch(`/vessels/${vesselId}/fuel-economy/${initial.id}`, body);
            } else {
                await api.post(`/vessels/${vesselId}/fuel-economy`, body);
            }
            onSaved();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="fuel-economy-modal"
            className="bm-modal-overlay"
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "grid", placeItems: "center", padding: 20, zIndex: 100 }}
            onClick={onClose}
        >
            <div className="bm-card" style={{ padding: 28, maxWidth: 440, width: "100%" }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
                    <h3 className="bm-serif" style={{ fontSize: 24, margin: 0, fontWeight: 500 }}>
                        {initial ? "Edit" : "Add"} RPM row
                    </h3>
                    <button onClick={onClose} className="bm-btn bm-btn-ghost" style={{ padding: 6 }} aria-label="Close">
                        <X size={16} />
                    </button>
                </div>
                <p style={{ color: "var(--bm-text-muted)", fontSize: 13, margin: "0 0 18px" }}>
                    Enter the values you measured at a steady throttle. Knots, MPG, and nMPG are computed automatically.
                </p>
                <div style={{ marginBottom: 12 }}>
                    <label className="bm-label">RPM</label>
                    <input
                        data-testid="fe-rpm-input"
                        className="bm-input"
                        type="number"
                        min="0"
                        step="50"
                        value={rpm}
                        onChange={(e) => setRpm(e.target.value)}
                        placeholder="e.g. 2500"
                        autoFocus
                    />
                </div>
                {isMultiEngine && (
                    <div style={{ marginBottom: 12 }}>
                        <label className="bm-label">Engine</label>
                        <select
                            data-testid="fe-engine-select"
                            className="bm-input"
                            value={engineId || ""}
                            onChange={(e) => setEngineId(e.target.value)}
                        >
                            <option value="">Unassigned (legacy)</option>
                            {engines.map((eng) => (
                                <option key={eng.id} value={eng.id}>{eng.label}</option>
                            ))}
                        </select>
                    </div>
                )}
                <div style={{ marginBottom: 12 }}>
                    <label className="bm-label">{fuelInputLabel(fuelUnit)} (fuel burn)</label>
                    <input
                        data-testid="fe-fuel-input"
                        className="bm-input"
                        type="number"
                        step="0.1"
                        min="0"
                        value={fuel}
                        onChange={(e) => setFuel(e.target.value)}
                        placeholder={fuelUnit === "lph" ? "e.g. 14.4" : "e.g. 3.8"}
                    />
                </div>
                <div style={{ marginBottom: 18 }}>
                    <label className="bm-label">{speedInputLabel(speedUnit)} (speed)</label>
                    <input
                        data-testid="fe-speed-input"
                        className="bm-input"
                        type="number"
                        step="0.1"
                        min="0"
                        value={speed}
                        onChange={(e) => setSpeed(e.target.value)}
                        placeholder={speedUnit === "kph" ? "e.g. 15.9" : speedUnit === "knots" ? "e.g. 8.6" : "e.g. 9.9"}
                    />
                </div>
                {err && <div data-testid="fe-modal-err" style={{ color: "var(--bm-bad)", marginBottom: 12, fontSize: 13 }}>{err}</div>}
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                    <button data-testid="fe-cancel-btn" className="bm-btn bm-btn-ghost" onClick={onClose}>Cancel</button>
                    <button data-testid="fe-save-btn" className="bm-btn bm-btn-gold" onClick={save} disabled={busy || !rpm || !fuel || !speed}>
                        <Save size={15} /> {busy ? "Saving…" : initial ? "Save changes" : "Add row"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Color palette for per-engine series (cycles for >5 engines).
const ENGINE_COLORS = [
    "var(--bm-gold)",   // engine 1 — gold
    "var(--bm-blue)",   // engine 2 — blue
    "#a78bfa",          // engine 3 — violet
    "#f97316",          // engine 4 — orange
    "#22d3ee",          // engine 5 — cyan
    "#ef4444",          // engine 6 — red
];

// Compute the Y value (MPG or L/100km) for one row given the unit settings.
function _yForRow(r, useL100km) {
    if (useL100km) {
        const lph = r.gph * L_PER_GAL;
        const kph = r.mph * KM_PER_MI;
        return kph > 0 ? (lph / kph) * 100 : 0;
    }
    return r.mph / r.gph;
}

// Lightweight inline SVG chart — MPG (or L/100km) vs RPM. When `showPerEngine`
// is true and the rows contain engine_ids, draws one line per engine for an
// at-a-glance port-vs-starboard comparison. Each engine series gets its own
// sweet-spot marker.
export function MpgChart({ rows, fuelUnit, speedUnit, compact = false, engines = [], showPerEngine = false }) {
    const useL100km = fuelUnit === "lph" && speedUnit === "kph";
    const label = economyLabel(fuelUnit, speedUnit);

    // Decide whether to render per-engine. Only when explicitly requested AND
    // we have at least one engine whose rows have engine_id set.
    const perEngine = showPerEngine && engines.length >= 2 && rows.some((r) => r.engine_id);

    // Build the series array. Each series = { engine, color, rows, yVals }.
    const series = useMemo(() => {
        const valid = rows.filter((r) => r.gph > 0 && r.mph > 0);
        if (!perEngine) {
            const sorted = [...valid].sort((a, b) => a.rpm - b.rpm);
            return [{ engine: null, color: "var(--bm-gold)", rows: sorted, yVals: sorted.map((r) => _yForRow(r, useL100km)) }];
        }
        return engines
            .map((eng, i) => {
                const r = valid.filter((row) => row.engine_id === eng.id).sort((a, b) => a.rpm - b.rpm);
                return { engine: eng, color: ENGINE_COLORS[i % ENGINE_COLORS.length], rows: r, yVals: r.map((row) => _yForRow(row, useL100km)) };
            })
            .filter((s) => s.rows.length >= 2);
    }, [rows, engines, perEngine, useL100km]);

    if (series.length === 0 || series.every((s) => s.rows.length < 2)) return null;

    // Compute global axis bounds across all series
    const allRows = series.flatMap((s) => s.rows);
    const allY = series.flatMap((s) => s.yVals);
    const xMin = Math.min(...allRows.map((r) => r.rpm));
    const xMax = Math.max(...allRows.map((r) => r.rpm));
    const yMin = Math.min(...allY);
    const yMax = Math.max(...allY);
    const yRange = yMax - yMin || 1;
    const xRange = xMax - xMin || 1;

    const W = compact ? 320 : 700;
    const H = compact ? 120 : 240;
    const PAD = { l: 50, r: 18, t: 32, b: 28 };
    const xPx = (rpm) => PAD.l + ((rpm - xMin) / xRange) * (W - PAD.l - PAD.r);
    const yPx = (v) => PAD.t + (1 - (v - yMin) / yRange) * (H - PAD.t - PAD.b);

    // Per-series sweet spot (best economy = highest MPG OR lowest L/100km)
    const seriesWithSweet = series.map((s) => {
        if (s.rows.length < 2) return { ...s, sweet: null, sweetY: null, sweetIdx: -1 };
        const sweetIdx = useL100km ? s.yVals.indexOf(Math.min(...s.yVals)) : s.yVals.indexOf(Math.max(...s.yVals));
        return { ...s, sweetIdx, sweet: s.rows[sweetIdx], sweetY: s.yVals[sweetIdx] };
    });

    return (
        <div data-testid="mpg-chart" className="bm-card" style={{ padding: compact ? 14 : 22 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                <BarChart3 size={compact ? 14 : 18} color="var(--bm-gold)" />
                <h3 className="bm-serif" style={{ fontSize: compact ? 14 : 18, margin: 0, fontWeight: 500 }}>
                    {label} vs RPM {compact ? "" : perEngine ? "— per engine" : "— find your sweet spot"}
                </h3>
                {perEngine && !compact && (
                    <div data-testid="mpg-chart-legend" style={{ display: "flex", gap: 14, marginLeft: "auto", flexWrap: "wrap" }}>
                        {seriesWithSweet.map((s) => (
                            <div key={s.engine.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--bm-text-muted)" }}>
                                <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: s.color }} />
                                <span style={{ color: s.color, fontWeight: 600 }}>{s.engine.label}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }} aria-label={`${label} versus RPM line chart`}>
                {/* Grid */}
                {[0, 0.25, 0.5, 0.75, 1].map((p) => (
                    <line key={p} x1={PAD.l} x2={W - PAD.r} y1={PAD.t + p * (H - PAD.t - PAD.b)} y2={PAD.t + p * (H - PAD.t - PAD.b)} stroke="rgba(28,63,99,0.35)" strokeWidth="1" />
                ))}
                {/* Axes labels */}
                <text x={PAD.l - 8} y={PAD.t + 4} fontSize="13" textAnchor="end" fill="var(--bm-text-muted)">{(useL100km ? yMin : yMax).toFixed(1)}</text>
                <text x={PAD.l - 8} y={H - PAD.b + 2} fontSize="13" textAnchor="end" fill="var(--bm-text-muted)">{(useL100km ? yMax : yMin).toFixed(1)}</text>
                <text x={PAD.l} y={H - 8} fontSize="13" fill="var(--bm-text-muted)">{`${xMin} RPM`}</text>
                <text x={W - PAD.r} y={H - 8} fontSize="13" textAnchor="end" fill="var(--bm-text-muted)">{`${xMax} RPM`}</text>
                <text x={4} y={PAD.t + (H - PAD.t - PAD.b) / 2} fontSize="13" fill="var(--bm-text-muted)" transform={`rotate(-90 14 ${PAD.t + (H - PAD.t - PAD.b) / 2})`} textAnchor="middle">{label}</text>
                {/* Series (one polyline per engine, or a single line) */}
                {seriesWithSweet.map((s, sIdx) => {
                    if (s.rows.length < 2) return null;
                    const path = s.rows.map((r, i) => `${i === 0 ? "M" : "L"} ${xPx(r.rpm).toFixed(1)} ${yPx(s.yVals[i]).toFixed(1)}`).join(" ");
                    return (
                        <g key={s.engine?.id || `series-${sIdx}`} data-testid={`mpg-series-${s.engine ? s.engine.id : "all"}`}>
                            <path d={path} fill="none" stroke={s.color} strokeWidth="2.5" />
                            {s.rows.map((r, i) => (
                                <circle key={r.id} cx={xPx(r.rpm)} cy={yPx(s.yVals[i])} r={i === s.sweetIdx ? 6 : 4} fill={i === s.sweetIdx ? "var(--bm-good)" : s.color} stroke="var(--bm-bg)" strokeWidth="2" />
                            ))}
                            {s.sweet && (
                                <text x={xPx(s.sweet.rpm)} y={yPx(s.sweetY) - 12} fontSize="13" textAnchor="middle" fill="var(--bm-good)" fontWeight="700">
                                    {`${s.sweet.rpm} · ${s.sweetY.toFixed(1)}`}
                                </text>
                            )}
                        </g>
                    );
                })}
            </svg>
            {!compact && (
                <div style={{ marginTop: 8, fontSize: 12, color: "var(--bm-text-muted)" }}>
                    {seriesWithSweet.filter((s) => s.sweet).map((s, i, arr) => (
                        <span key={s.engine?.id || i} style={{ marginRight: 14, display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <Fuel size={12} color={s.color} />
                            {s.engine ? <strong style={{ color: s.color }}>{s.engine.label}:</strong> : "Sweet spot:"}{" "}
                            <strong style={{ color: "var(--bm-good)" }}>{s.sweet.rpm} RPM</strong> at <strong style={{ color: "var(--bm-good)" }}>{s.sweetY.toFixed(2)} {label}</strong>
                            {i < arr.length - 1 ? "" : "."}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
